/**
 * Autonomous budget pacing for the Budgets tab.
 *
 * Paints the last live tick from the server (or localStorage), then follows
 * Mon/Thu cron without a click. Refresh still recomputes a dry-run.
 * Share / proposed daily are editable; Save split writes the mix to Planner
 * so the Mon/Thu pacing job honors it. This card never spends.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Gauge, Loader2, RefreshCw, Save, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Badge } from '@/components/ui/badge';
import type { AutonomousPacingSummary } from './BudgetMoneyStrip';
import { PERSISTED_GC_TIME_MS } from '@/lib/budgetPacingPersist';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  getLastAutonomousPacingPlan,
  getPacingCeiling,
  previewAutonomousPacing,
  updateAutonomousShares,
  updatePacingCeiling,
  type AutonomousPacingApplyResponse,
  type AutonomousPacingLsa,
  type AutonomousPacingLsaWrite,
  type GoogleBudgetSplit,
  type SharedBudgetGroup,
} from '@/services/googleAdsMonthlyService';
import {
  campaignChannelLabel,
  cronZoneLabel,
  nextAutonomousCron,
} from './budgetPacing';

export const AUTONOMOUS_PACING_QUERY_KEY = 'google-ads-autonomous-pacing';
export const PACING_CEILING_QUERY_KEY = 'google-ads-pacing-ceiling';
const AUTONOMOUS_PACING_STORAGE_PREFIX = 'agileco.autonomous-pacing.';

function storageKey(tenantId: string): string {
  return `${AUTONOMOUS_PACING_STORAGE_PREFIX}${tenantId}`;
}

export function readStoredPacingPlan(
  tenantId: string,
): AutonomousPacingApplyResponse | undefined {
  return readStoredPacingSnapshot(tenantId)?.plan;
}

export function readStoredPacingSnapshot(
  tenantId: string,
): { plan: AutonomousPacingApplyResponse; savedAt?: string } | undefined {
  if (!tenantId || typeof localStorage === 'undefined') return undefined;
  try {
    const raw = localStorage.getItem(storageKey(tenantId));
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as {
      plan?: AutonomousPacingApplyResponse;
      savedAt?: string;
    } & Partial<AutonomousPacingApplyResponse>;
    // The legacy shape stored the response at the top level; the current one
    // nests it under `plan`. Narrowing on `adjustments` is what distinguishes
    // them, and the cast records that — the intersection type cannot express
    // "this branch is the full response".
    const plan =
      parsed.plan ??
      (parsed.adjustments
        ? (parsed as AutonomousPacingApplyResponse)
        : undefined);
    if (!plan || !Array.isArray(plan.adjustments)) return undefined;
    if (plan.tenant_id && plan.tenant_id !== tenantId) return undefined;
    return { plan, savedAt: parsed.savedAt };
  } catch {
    return undefined;
  }
}

export function writeStoredPacingPlan(
  tenantId: string,
  plan: AutonomousPacingApplyResponse,
  savedAt?: string,
): void {
  if (!tenantId || typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(
      storageKey(tenantId),
      JSON.stringify({
        savedAt: savedAt ?? new Date().toISOString(),
        plan,
      }),
    );
  } catch {
    // Quota or private mode — the in-memory React Query cache still holds.
  }
}

const LAST_PLAN_POLL_MS = 30_000;

function newestIso(a?: string, b?: string): string | undefined {
  if (!a) return b;
  if (!b) return a;
  const ta = Date.parse(a);
  const tb = Date.parse(b);
  if (!Number.isFinite(ta)) return b;
  if (!Number.isFinite(tb)) return a;
  return tb > ta ? b : a;
}

function changeIsNewer(
  runAt?: string | null,
  planAt?: string | null,
): boolean {
  if (!runAt) return false;
  if (!planAt) return true;
  return new Date(runAt) > new Date(planAt);
}

interface AutonomousPacingPreviewCardProps {
  tenantId: string;
  accountTimeZone?: string | null;
  cycleDaysLeft?: number | null;
  cycleStart?: string;
  cycleEnd?: string;
  snapshotLsa?: AutonomousPacingLsa | null;
  /** Display only: add the LSA account into Current daily / Target daily. */
  combinedTotals?: boolean;
  /** The money row at the top of the page draws the daily tiles; the card hands its numbers up. */
  onSummaryChange?: (summary: AutonomousPacingSummary | null) => void;
  /**
   * Bumped by the parent after Ads account settings save. The preview query is
   * disabled once a plan is seeded, so invalidation cannot reach it; a new
   * token (> 0) refetches through the observer, the one path that works.
   */
  refreshToken?: number;
  /** Server clock of that save; plans saved before it must not overwrite the fresh preview. */
  scopeSavedAt?: string | null;
  /** Reports while the post-save refetch is on the wire, and only then. */
  onScopeRefreshingChange?: (refreshing: boolean) => void;
  /** Planner's two Google monthly lines; drawn on the parent envelope rows when totals are combined. */
  googleSplit?: GoogleBudgetSplit | null;
  /** "Change split" on the LSA row; the parent opens Account settings. */
  onChangeSplit?: () => void;
}

interface DraftRow {
  campaign_id: string;
  campaign_name: string;
  advertising_channel_type?: string;
  share: number;
  current_amount_micros: number;
  proposed_amount_micros: number;
  relearn_clamped: boolean;
  write_status?: string | null;
  skip_reason?: string | null;
  ceiling_bound?: boolean;
  ceiling_max_micros?: number | null;
  /** Non-null when this campaign has its own monthly budget rather than a
   *  share of the account total. */
  fixed_monthly_usd?: number | null;
}

function formatUsdFromMicros(micros: number | null | undefined): string {
  if (typeof micros !== 'number' || !Number.isFinite(micros)) return '—';
  return (micros / 1_000_000).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  });
}

function formatWholeUsdFromMicros(micros: number | null | undefined): string {
  if (typeof micros !== 'number' || !Number.isFinite(micros)) return '—';
  return (micros / 1_000_000).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

function formatDollars(value: number): string {
  return `$${value.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}

/** Whole percent of the Planner parent line, or null when either side is unknown. */
function percentOfGoogle(part: number | null | undefined, total: number | null | undefined): number | null {
  if (typeof part !== 'number' || typeof total !== 'number' || !(total > 0)) return null;
  return Math.round((part / total) * 100);
}

// The writer's LSA daily floor; at or under it "unchanged" means "already at the floor".
const LSA_FLOOR_MICROS = 140_000;

/** Why the LSA writer wrote nothing, in words. Never the raw reason. */
function lsaSkipLabel(write: AutonomousPacingLsaWrite | null | undefined): string {
  const reason = write?.skip_reason ?? '';
  const proposed = write?.proposed_amount_micros;
  if (reason === 'lsa_unchanged' && typeof proposed === 'number') {
    const daily = formatUsdFromMicros(proposed);
    return proposed <= LSA_FLOOR_MICROS
      ? `At the ${daily}/day floor, nothing to write`
      : `Already at ${daily}/day, nothing to write`;
  }
  if (reason === 'already_ticked_this_window') return 'Already written this window';
  if (reason === 'lsa_autopace_off') return 'Core is not managing this LSA budget';
  if (reason === 'lsa_step_too_large' && typeof proposed === 'number') {
    return `Held: ${formatUsdFromMicros(proposed)}/day is more than 3x the live ${formatUsdFromMicros(write?.current_amount_micros)}/day`;
  }
  return 'not written';
}

function extractError(err: unknown): string {
  return (
    (err as { response?: { data?: { detail?: string } } })?.response?.data
      ?.detail ?? String(err)
  );
}

function skipReasonToast(reason: string): string {
    if (reason === 'shared_campaign_budget') {
    return 'This account uses one shared budget across campaigns — pacing left it alone.';
  }
  if (reason === 'already_ticked_this_window') {
    return 'This window already ran — proposed daily is the live current until the next Mon/Thu tick.';
  }
  if (reason === 'spend_data_stale') {
    return 'Spend data is stale. Pacing changed nothing for this client.';
  }
  if (reason === 'live_spend_unknown') {
    return 'Live Google Ads spend could not be read. Pacing changed nothing for this client.';
  }
  if (reason === 'spend_unconfirmed') {
    return "Spend can't be confirmed. Pacing changed nothing for this client.";
  }
  if (reason === 'missing_ceiling_n') {
    return 'PMax cannot raise until a ceiling N is set (1.0–3.0 × mapped daily).';
  }
  return `Pacing skipped: ${reason}`;
}

interface PacingRefusal {
  title: string;
  why: string;
  steps: string[];
}

const NEXT_TICK_STEP = 'Core plans again on the next Monday or Thursday tick, or press Refresh pacing after the change.';

/** Plain words and a way out for every reason Core refuses to plan an account. */
export const PACING_REFUSALS: Record<string, PacingRefusal> = {
  unverified_lsa_remaining: {
    title: 'LSA is live on this account, but Planner has no LSA line',
    why: 'The Google Ads account has a Local Services campaign, and the client\'s Planner pacing has no Google LSA line. Core will not guess how much of the budget LSA takes, so it plans nothing rather than overspend Ads.',
    steps: [
      'Add a Google LSA line to this client\'s Planner pacing, even a small one, or',
      'Pause or remove the Local Services campaign in Google Ads if it should not be running.',
      NEXT_TICK_STEP,
    ],
  },
  missing_planner_contract: {
    title: 'No Planner contract for this client',
    why: 'Core paces to the Planner budget. Without a mapped client and a contract budget there is no target to pace toward.',
    steps: ['Map this client to Planner and set the contract budget there.', NEXT_TICK_STEP],
  },
  google_ads_not_in_pacing: {
    title: 'Google Ads is not in this client\'s Planner pacing',
    why: 'Planner funds another platform for this client. Core does not invent a Google Ads budget from live daily caps.',
    steps: ['Add Google Ads to the client\'s Planner pacing if it should be paced, otherwise nothing to do.'],
  },
  no_search_campaigns: {
    title: 'No campaign Core can pace',
    why: 'Core writes Search and Performance Max campaigns that are enabled and have their own budget. None qualify right now.',
    steps: ['Enable a Search or PMax campaign, or give a shared-budget campaign its own budget.', NEXT_TICK_STEP],
  },
  end_of_cycle: {
    title: 'The billing cycle has ended',
    why: 'No days are left to spread the remaining budget over.',
    steps: ['Nothing to do. Core resumes on the first tick of the next cycle.'],
  },
  non_positive_target: {
    title: 'Nothing left to pace',
    why: 'Planner remaining is zero or negative for this cycle, so the daily target is zero.',
    steps: ['Raise the Planner budget or add carryover if the client should keep spending.', NEXT_TICK_STEP],
  },
  blast_radius: {
    title: 'The change is too large to apply unattended',
    why: 'The plan would move more budget in one tick than Core is allowed to move without a person confirming.',
    steps: ['Review the campaign table below and Save split by hand, or wait for the next tick when the gap is smaller.'],
  },
  campaign_scope_unresolved: {
    title: 'The campaign filter matches nothing',
    why: 'Account settings names campaigns to pace and none of them exist on the account.',
    steps: ['Fix or clear "Only campaigns named" in Account settings.', NEXT_TICK_STEP],
  },
  persist_unavailable: {
    title: 'Core could not record the run',
    why: 'Core refuses to write budgets when it cannot write its own run row first.',
    steps: ['Press Refresh pacing. If it repeats, tell engineering; nothing to change on the account.'],
  },
  tenant_not_active: {
    title: 'This client is offboarded',
    why: 'Core does not read or write Google Ads for a client whose status is not active. The account still has a customer id on file, so it appears here, but every pacing path refuses it.',
    steps: [
      'If this client is back, set Client status to Active under Settings, Business info.',
      'If not, clear the Google Ads and LSA ids and the Planner mapping in Account settings, archive the contract in Planner, and remove the account from the MCC.',
    ],
  },
  tenant_paused: {
    title: 'This client is paused',
    why: 'Someone parked this client on purpose. Core keeps the account on file but makes no Google Ads write while the status is paused.',
    steps: ['When the client resumes, set Client status to Active under Settings, Business info.', NEXT_TICK_STEP],
  },
  spend_data_stale: {
    title: 'Spend data is stale',
    why: 'The Planner\'s recorded spend has stopped updating for this client, so Core cannot tell how much budget is really left. It changes nothing on the account rather than pace off old figures.',
    steps: [
      'Check current spend in Google Ads; the Budget Pacing figures are out of date.',
      'Tell engineering the Planner spend sync has stopped for this client.',
      'Core plans again on the first Monday or Thursday tick after the spend updates.',
    ],
  },
  live_spend_unknown: {
    title: 'Live spend could not be read',
    why: 'Google Ads sent back spend Core could not read, so it cannot check whether the Planner\'s recorded spend is current. It changes nothing on the account rather than pace off figures it cannot check.',
    steps: [
      'Press Refresh pacing. If it repeats, tell engineering that Core could not read live spend for this client.',
      NEXT_TICK_STEP,
    ],
  },
  spend_unconfirmed: {
    title: 'Spend can\'t be confirmed',
    why: 'There is no Google Ads spend this cycle, and the Planner\'s recorded spend last changed before the cycle started, so Core cannot tell whether the client is paused or the Planner has stopped updating. It changes nothing on the account until one of them moves.',
    steps: [
      'Check in Google Ads whether the campaigns are meant to be paused.',
      'If they should be spending, check that the Planner shows this cycle\'s spend, and tell engineering if it does not.',
      'Core plans again on the first Monday or Thursday tick after Google Ads spends or the Planner updates.',
    ],
  },
  flag_off: {
    title: 'Autonomous pacing is switched off here',
    why: 'The pacing feature flag is off on this environment.',
    steps: ['Nothing to do on the account. Engineering owns the flag.'],
  },
};

function capitalize(text: string): string {
  const trimmed = text.replace(/\.+$/, '');
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

const PLAIN_SKIPS = new Set(['shared_campaign_budget', 'already_ticked_this_window']);

export function pacingRefusal(plan: AutonomousPacingApplyResponse | null): (PacingRefusal & { reason: string }) | null {
  if (!plan || plan.status !== 'skipped' || !plan.skip_reason) return null;
  if (PLAIN_SKIPS.has(plan.skip_reason)) return null;
  const known = PACING_REFUSALS[plan.skip_reason];
  // hf24: the backend's plain sentence ("spend data is stale since 2026-10-21")
  // leads the explanation when it sends one.
  const detail = plan.skip_detail?.trim();
  const copy = known ?? {
    title: 'Core did not plan this account',
    why: `Core stopped with reason "${plan.skip_reason}".`,
    steps: ['Ask engineering what this reason means; nothing to change on the account yet.'],
  };
  return {
    reason: plan.skip_reason,
    ...copy,
    why: detail ? `${capitalize(detail)}. ${copy.why}` : copy.why,
  };
}

function PacingRefusalPanel({ refusal }: { refusal: PacingRefusal & { reason: string } }) {
  return (
    <div
      className="rounded-md border border-coral/40 bg-coral-light px-4 py-3 text-sm"
      data-testid="pacing-refusal"
    >
      <div className="font-semibold text-coral-dark">Core did not plan this account: {refusal.title}</div>
      <p className="mt-1 text-muted-foreground">{refusal.why}</p>
      <ol className="mt-2 list-decimal space-y-1 pl-5">
        {refusal.steps.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
      <p className="mt-2 text-[11px] text-muted-foreground">Reason code: {refusal.reason}</p>
    </div>
  );
}

function isSharedBudgetSkip(plan: AutonomousPacingApplyResponse | null): boolean {
  return plan?.skip_reason === 'shared_campaign_budget';
}

function sharedCampaignCount(groups: SharedBudgetGroup[]): number {
  const ids = new Set<string>();
  for (const group of groups) {
    for (const campaign of group.campaigns) {
      if (campaign.campaign_id) ids.add(campaign.campaign_id);
    }
  }
  if (ids.size > 0) return ids.size;
  return groups.reduce((sum, group) => sum + (group.campaign_count || 0), 0);
}

function toastPreviewResult(result: AutonomousPacingApplyResponse): void {
  if (result.skip_reason) {
    toast.info(skipReasonToast(result.skip_reason));
    return;
  }
  const wouldWrite = result.adjustments.filter(
    (row) => row.write_status === 'dry_run',
  ).length;
  toast.success(
    `Pacing refreshed: ${wouldWrite} write(s) would apply` +
      (result.errors > 0 ? ` — ${result.errors} error(s)` : ''),
  );
}

function draftsFromPlan(plan: AutonomousPacingApplyResponse | null): DraftRow[] {
  if (!plan) return [];
  return plan.adjustments.map((row) => ({
    campaign_id: row.campaign_id,
    campaign_name: row.campaign_name,
    advertising_channel_type: row.advertising_channel_type,
    share: row.share,
    current_amount_micros: row.current_amount_micros,
    proposed_amount_micros: row.proposed_amount_micros,
    relearn_clamped: row.relearn_clamped,
    write_status: row.write_status,
    skip_reason: row.skip_reason,
    ceiling_bound: row.ceiling_bound,
    ceiling_max_micros: row.ceiling_max_micros,
    fixed_monthly_usd: row.fixed_monthly_usd,
  }));
}

function withProposed(
  rows: DraftRow[],
  targetMicros: number,
): DraftRow[] {
  return rows.map((row) => ({
    ...row,
    proposed_amount_micros: Math.round(row.share * targetMicros),
    relearn_clamped: false,
  }));
}

function setShareKeepingHundred(
  rows: DraftRow[],
  campaignId: string,
  nextShare: number,
  targetMicros: number,
): DraftRow[] {
  const clamped = Math.min(1, Math.max(0, nextShare));
  const others = rows.filter((row) => row.campaign_id !== campaignId);
  const remaining = 1 - clamped;
  const otherSum = others.reduce((sum, row) => sum + row.share, 0);
  const next = rows.map((row) => {
    if (row.campaign_id === campaignId) {
      return { ...row, share: clamped };
    }
    if (others.length === 0) return row;
    if (otherSum <= 0) {
      return { ...row, share: remaining / others.length };
    }
    return { ...row, share: (row.share / otherSum) * remaining };
  });
  return withProposed(next, targetMicros);
}

function setDailyKeepingHundred(
  rows: DraftRow[],
  campaignId: string,
  nextDailyUsd: number,
  targetMicros: number,
): DraftRow[] {
  if (targetMicros <= 0) return rows;
  const nextShare = (nextDailyUsd * 1_000_000) / targetMicros;
  return setShareKeepingHundred(rows, campaignId, nextShare, targetMicros);
}

function sharePercents(rows: DraftRow[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const row of rows) {
    out[row.campaign_id] = Math.round(row.share * 1000) / 10;
  }
  return out;
}

function SharedBudgetSkipPanel({
  groups,
  potMicros,
  exclusiveAlsoPaced = false,
}: {
  groups: SharedBudgetGroup[];
  potMicros: number;
  exclusiveAlsoPaced?: boolean;
}) {
  const campaignCount = sharedCampaignCount(groups);
  const potLabel = formatUsdFromMicros(
    groups.length === 1 ? groups[0].daily_budget_micros : potMicros,
  );

  return (
    <div className="overflow-hidden rounded-md border border-border bg-warm-bg">
      <div className="flex items-start gap-3 border-b border-border px-4 py-3">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-teal-light text-coral-dark">
          <Wallet className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              One wallet
            </p>
            <Badge variant="outline">Not paced — shared budget</Badge>
          </div>
          <h3 className="mt-1 text-base font-semibold">Shared campaign budget</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {campaignCount > 0
              ? `Shared daily budget across ${campaignCount} campaign${campaignCount === 1 ? '' : 's'} — pacing requires per-campaign budgets.`
              : 'Shared daily budget — pacing requires per-campaign budgets.'}
            {' '}
            Google decides who spends from this pot. We do not write it, because one change would move every campaign on it.
            {exclusiveAlsoPaced
              ? ' Exclusive campaigns below are still paced; this pot is reserved out of remaining.'
              : ''}
          </p>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
            Shared pot
          </div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-coral-dark">
            {potLabel}
            <span className="ml-1 text-xs font-normal text-muted-foreground">
              / day
            </span>
          </div>
        </div>
      </div>

      {groups.length > 0 ? (
        <div className="divide-y divide-border">
          {groups.map((group) => (
            <div key={group.budget_resource_name} className="px-4 py-3">
              {groups.length > 1 ? (
                <div className="mb-2 flex items-baseline justify-between gap-3 text-xs">
                  <span className="text-muted-foreground">
                    Budget {group.budget_id || group.budget_resource_name.split('/').pop()}
                  </span>
                  <span className="tabular-nums font-medium">
                    {formatUsdFromMicros(group.daily_budget_micros)} / day ·{' '}
                    {group.campaign_count} campaign{group.campaign_count === 1 ? '' : 's'}
                  </span>
                </div>
              ) : (
                <p className="mb-2 text-xs text-muted-foreground">
                  Campaigns on this pot — not {campaignCount} separate dailies
                </p>
              )}
              <ul className="grid gap-2 sm:grid-cols-2">
                {group.campaigns.map((campaign) => (
                  <li
                    key={campaign.campaign_id}
                    className="flex items-center justify-between gap-2 rounded-md border border-border/70 bg-background px-3 py-2"
                  >
                    <span className="truncate text-sm font-medium">
                      {campaign.campaign_name}
                    </span>
                    <Badge variant="outline">
                      {campaignChannelLabel(campaign.advertising_channel_type)}
                    </Badge>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : (
        <p className="px-4 py-3 text-sm text-muted-foreground">
          Refresh pacing to list the campaigns on this shared wallet.
        </p>
      )}
    </div>
  );
}

export function AutonomousPacingPreviewCard({
  tenantId,
  snapshotLsa,
  combinedTotals = false,
  onSummaryChange,
  refreshToken = 0,
  scopeSavedAt = null,
  onScopeRefreshingChange,
  googleSplit = null,
  onChangeSplit,
}: AutonomousPacingPreviewCardProps) {
  const queryClient = useQueryClient();
  const storedSnapshot = useMemo(
    () => readStoredPacingSnapshot(tenantId),
    [tenantId],
  );
  const lastTick = useQuery({
    queryKey: [AUTONOMOUS_PACING_QUERY_KEY, 'last-plan', tenantId],
    queryFn: () => getLastAutonomousPacingPlan({ tenantId }),
    enabled: Boolean(tenantId),
    retry: false,
    gcTime: PERSISTED_GC_TIME_MS,
    refetchInterval: LAST_PLAN_POLL_MS,
    refetchOnWindowFocus: true,
  });
  const lastTickReady = lastTick.isFetched || lastTick.isError;
  const serverPlan = lastTick.data?.plan ?? undefined;
  const serverSavedAt = lastTick.data?.saved_at ?? undefined;
  // "The account moved", not "a tick happened": a guarded re-run is not a
  // tick and never the run of record, but it did move live budgets, so a
  // cached plan from before it is behind the account.
  const lastAccountChangeAt = newestIso(
    lastTick.data?.last_run_at ?? undefined,
    lastTick.data?.last_rerun_at ?? undefined,
  );
  const seedSavedAt = serverSavedAt ?? storedSnapshot?.savedAt;
  const seedPlan = serverPlan ?? storedSnapshot?.plan;
  const needsLiveRefresh = changeIsNewer(lastAccountChangeAt, seedSavedAt);
  const [planSavedAt, setPlanSavedAt] = useState<string | undefined>(
    storedSnapshot?.savedAt ?? serverSavedAt,
  );
  const pacing = useQuery({
    queryKey: [AUTONOMOUS_PACING_QUERY_KEY, tenantId],
    queryFn: async () => {
      const plan = await previewAutonomousPacing({ tenantId });
      const now = new Date().toISOString();
      writeStoredPacingPlan(tenantId, plan, now);
      setPlanSavedAt(now);
      return plan;
    },
    enabled: Boolean(tenantId) && lastTickReady && !seedPlan,
    initialData: seedPlan,
    initialDataUpdatedAt: seedPlan ? Date.now() : undefined,
    staleTime: Infinity,
    gcTime: PERSISTED_GC_TIME_MS,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const paintedSavedAt = useRef<string | null>(null);
  const syncedChangeAt = useRef<string | null>(null);
  const dirtyRef = useRef(false);
  // Account settings saved: the seeded plan predates them. Refetch through
  // the observer (the query is disabled while seeded) and remember when, so
  // the server-plan effect below cannot lay an older cron plan over the
  // fresh preview. Same shape as the "account moved" effect further down.
  const scopeRefreshToken = useRef(0);
  const scopeRefreshAt = useRef<string | null>(null);
  const scopeSavedAtRef = useRef<string | null>(null);
  scopeSavedAtRef.current = scopeSavedAt;
  // Set only around THIS refetch. refreshToken never returns to 0, so gating
  // on it alone would call every later refetch a settings recalculation
  // (Viktor, #2599). The observer's isFetching drives the re-renders.
  const scopeRefreshInFlight = useRef(false);
  useEffect(() => {
    if (!refreshToken || refreshToken === scopeRefreshToken.current) return;
    scopeRefreshToken.current = refreshToken;
    // Server clock of the save when the API sent one; the browser clock is
    // only the fallback for an older API.
    scopeRefreshAt.current = scopeSavedAtRef.current ?? new Date().toISOString();
    scopeRefreshInFlight.current = true;
    void pacing.refetch().finally(() => {
      scopeRefreshInFlight.current = false;
    });
  }, [refreshToken, pacing]);
  const scopeRefreshing = scopeRefreshInFlight.current && pacing.isFetching;
  useEffect(() => {
    onScopeRefreshingChange?.(scopeRefreshing);
  }, [scopeRefreshing, onScopeRefreshingChange]);
  useEffect(() => {
    if (!serverPlan) return;
    if (dirtyRef.current) return;
    if (scopeRefreshAt.current && !changeIsNewer(serverSavedAt, scopeRefreshAt.current)) return;
    if (serverSavedAt && paintedSavedAt.current === serverSavedAt) return;
    paintedSavedAt.current = serverSavedAt ?? 'plan';
    writeStoredPacingPlan(tenantId, serverPlan, serverSavedAt);
    if (serverSavedAt) setPlanSavedAt(serverSavedAt);
    queryClient.setQueryData([AUTONOMOUS_PACING_QUERY_KEY, tenantId], serverPlan);
  }, [serverPlan, serverSavedAt, queryClient, tenantId]);
  useEffect(() => {
    if (!lastTickReady || !needsLiveRefresh || !seedPlan) return;
    if (lastAccountChangeAt && syncedChangeAt.current === lastAccountChangeAt) return;
    if (lastAccountChangeAt) syncedChangeAt.current = lastAccountChangeAt;
    void pacing.refetch();
  }, [lastTickReady, needsLiveRefresh, lastAccountChangeAt, seedPlan, pacing]);

  const ceiling = useQuery({
    queryKey: [PACING_CEILING_QUERY_KEY, tenantId],
    queryFn: () => getPacingCeiling({ tenantId }),
    enabled: Boolean(tenantId),
    retry: false,
    gcTime: PERSISTED_GC_TIME_MS,
  });
  const [draftN, setDraftN] = useState('');
  useEffect(() => {
    const n = ceiling.data?.n;
    setDraftN(n == null ? '' : String(n));
  }, [ceiling.data?.n]);

  const lastPreview = pacing.data ?? null;
  const targetMicros = lastPreview?.target_account_daily_micros ?? 0;
  const [drafts, setDrafts] = useState<DraftRow[]>(() => draftsFromPlan(lastPreview));

  useEffect(() => {
    setDrafts(draftsFromPlan(lastPreview));
  }, [lastPreview]);

  const baseline = useMemo(() => draftsFromPlan(lastPreview), [lastPreview]);
  const dirty = JSON.stringify(sharePercents(drafts)) !== JSON.stringify(sharePercents(baseline));
  dirtyRef.current = dirty;
  // Campaigns on their own monthly budget are not a share of anything, so they
  // are excluded from the allocation total. Counting them would report "0%
  // allocated" for a client whose budgets are entirely fixed and correct.
  const shareTotal = drafts.reduce(
    (sum, row) => (row.fixed_monthly_usd != null ? sum : sum + row.share),
    0,
  );
  const fixedCount = drafts.filter((row) => row.fixed_monthly_usd != null).length;

  const saveShares = useMutation({
    mutationFn: () =>
      updateAutonomousShares({
        tenantId,
        shares: Object.fromEntries(
          drafts.map((row) => [row.campaign_id, Math.round(row.share * 1000) / 10]),
        ),
      }),
    onSuccess: async () => {
      toast.success('Saved campaign split to Planner');
      const result = await pacing.refetch();
      if (result.data) toastPreviewResult(result.data);
      await queryClient.invalidateQueries({
        queryKey: ['google-ads-budget-pacing', tenantId],
      });
    },
    onError: (err: unknown) => {
      toast.error(`Could not save split: ${extractError(err)}`);
    },
  });

  const saveCeiling = useMutation({
    mutationFn: () => {
      const trimmed = draftN.trim();
      if (trimmed === '') {
        return updatePacingCeiling({ tenantId, n: null });
      }
      const next = Number.parseFloat(trimmed);
      if (!Number.isFinite(next) || next < 1 || next > 3) {
        throw new Error('Ceiling N must be between 1.0 and 3.0');
      }
      return updatePacingCeiling({ tenantId, n: next });
    },
    onSuccess: async (saved) => {
      toast.success(
        saved.n == null
          ? 'Ceiling cleared — PMax will not raise'
          : `Ceiling saved: ${saved.n}× mapped daily`,
      );
      await queryClient.invalidateQueries({
        queryKey: [PACING_CEILING_QUERY_KEY, tenantId],
      });
      const result = await pacing.refetch();
      if (result.data) toastPreviewResult(result.data);
    },
    onError: (err: unknown) => {
      toast.error(`Could not save ceiling: ${extractError(err)}`);
    },
  });

  const resetBaselines = useMutation({
    mutationFn: () =>
      updatePacingCeiling({
        tenantId,
        n: ceiling.data?.n ?? null,
        resetBaselines: true,
      }),
    onSuccess: async () => {
      toast.success('Mapped dailies cleared — the next scheduled sweep will freeze the current split');
      await queryClient.invalidateQueries({
        queryKey: [PACING_CEILING_QUERY_KEY, tenantId],
      });
      const result = await pacing.refetch();
      if (result.data) toastPreviewResult(result.data);
    },
    onError: (err: unknown) => {
      toast.error(`Could not recompute baselines: ${extractError(err)}`);
    },
  });

  const onRefresh = async () => {
    const result = await pacing.refetch();
    if (result.error) {
      toast.error(`Pacing refresh failed: ${extractError(result.error)}`);
      return;
    }
    if (result.data) toastPreviewResult(result.data);
  };

  const firstLoad = pacing.isLoading && !lastPreview;
  const nextTick = nextAutonomousCron(new Date());
  const currentMicros = lastPreview?.current_account_daily_micros ?? 0;
  // The LSA line waits while an account settings save recomputes the plan.
  const recalculating = scopeRefreshing;
  const ratio =
    currentMicros > 0 ? Math.min(1.5, targetMicros / currentMicros) : 0;
  const sharedSkip = isSharedBudgetSkip(lastPreview);
  const heldThisWindow = lastPreview?.skip_reason === 'already_ticked_this_window';
  // Memoised: the summary effect depends on it, and a fresh object per render
  // would re-run the effect, set parent state, and loop on a refused account.
  const refusal = useMemo(() => pacingRefusal(lastPreview), [lastPreview]);
  const lsaReservationMicros =
    lastPreview?.lsa && lastPreview.lsa.available !== false &&
    typeof lastPreview.lsa.implied_daily_micros === 'number'
      ? lastPreview.lsa.implied_daily_micros
      : null;
  const lsaMonitor = lastPreview?.lsa ?? snapshotLsa ?? null;
  // The LSA account's live daily, as the writer read it this plan (null when
  // it skipped before reading). Same-vintage as the preview by construction.
  const lsaWriteBlock = lastPreview?.lsa_write ?? null;
  // current_amount_micros is the pre-write read; after a success the account
  // holds proposed_amount_micros, so that is the live daily until the next tick.
  const lsaCurrentMicros =
    lsaWriteBlock?.write_status === 'success' &&
    typeof lsaWriteBlock.proposed_amount_micros === 'number'
      ? lsaWriteBlock.proposed_amount_micros
      : typeof lsaWriteBlock?.current_amount_micros === 'number'
        ? lsaWriteBlock.current_amount_micros
        : null;
  const lsaPlanMicros =
    lsaWriteBlock &&
    (lsaWriteBlock.write_status === 'success' || lsaWriteBlock.write_status === 'dry_run') &&
    typeof lsaWriteBlock.proposed_amount_micros === 'number'
      ? lsaWriteBlock.proposed_amount_micros
      : typeof lastPreview?.lsa?.implied_daily_micros === 'number' && lastPreview?.lsa?.available !== false
        ? lastPreview.lsa.implied_daily_micros
        : null;
  const lsaPlanIsReservation =
    lsaPlanMicros != null &&
    !(lsaWriteBlock && (lsaWriteBlock.write_status === 'success' || lsaWriteBlock.write_status === 'dry_run'));
  const combine = combinedTotals && Boolean(lsaMonitor) && !sharedSkip;
  const combinedCurrentMicros = combine && lsaCurrentMicros != null ? currentMicros + lsaCurrentMicros : null;
  // What Core will actually set: the saved plan after clamps, not the
  // reservation it aims at and not the unsaved table edits (those re-derive
  // every row as share x target and would snap the headline to the
  // reservation). A skipped row keeps its current daily.
  const suggestedAdsMicros = sharedSkip
    ? currentMicros
    : baseline.reduce(
        (sum, row) =>
          sum + ((row.skip_reason ? row.current_amount_micros : row.proposed_amount_micros) || 0),
        0,
      );
  const suggestedMicros =
    combine && lsaPlanMicros != null ? suggestedAdsMicros + lsaPlanMicros : suggestedAdsMicros;
  const hasLsa = Boolean(lsaMonitor);
  // Parent envelope rows (combine on): the outside of the Google envelope and
  // its two inner lines, from Planner's monthly split.
  const adsPercentOfGoogle = percentOfGoogle(
    googleSplit?.google_ads_monthly_dollars,
    googleSplit?.total_monthly_dollars,
  );
  const lsaPercentOfGoogle = percentOfGoogle(
    googleSplit?.google_lsa_monthly_dollars,
    googleSplit?.total_monthly_dollars,
  );
  // Mirror `suggestedMicros`: LSA plan only, never the reservation fallback
  // (Viktor #2614: `lsaPlanMicros` deliberately refuses `implied_daily_micros`
  // when `lsa.available === false`, and a snapshot LSA mixes vintages in a
  // sum). LSA unknown reads ", partial", the same as the money strip's
  // Suggested tile, not a confident $0.
  const parentSuggestedKnown = suggestedMicros;
  const parentSuggestedPartial = combine && lsaPlanMicros == null;
  // The callback lives in a ref so an inline arrow from the parent (a new
  // identity every render) cannot re-fire the effect and loop through
  // parent state. Only the numbers are dependencies.
  const onSummaryChangeRef = useRef(onSummaryChange);
  useEffect(() => {
    onSummaryChangeRef.current = onSummaryChange;
  }, [onSummaryChange]);
  useEffect(() => {
    const emit = onSummaryChangeRef.current;
    if (!emit) return;
    if (!lastPreview) {
      emit(null);
      return;
    }
    emit({
      currentMicros: combinedCurrentMicros ?? currentMicros,
      currentAdsMicros: currentMicros,
      lsaCurrentMicros,
      suggestedMicros,
      suggestedAdsMicros,
      targetAdsMicros: targetMicros,
      lsaPlanMicros,
      lsaPlanIsReservation,
      combine,
      sharedSkip,
      hasLsa,
      unsavedEdits: dirty,
      heldThisWindow,
      refusalReason: refusal?.reason ?? null,
      refusalTitle: refusal?.title ?? null,
      lsaReservationMicros,
    });
  }, [
    lastPreview,
    combinedCurrentMicros,
    currentMicros,
    lsaCurrentMicros,
    suggestedMicros,
    suggestedAdsMicros,
    targetMicros,
    lsaPlanMicros,
    lsaPlanIsReservation,
    combine,
    sharedSkip,
    hasLsa,
    dirty,
    heldThisWindow,
    refusal,
    lsaReservationMicros,
  ]);
  // (refusal is memoised on lastPreview above; identity is stable per plan.)
  const sharedGroups = lastPreview?.shared_budgets ?? [];
  const hasSharedPots = sharedGroups.length > 0;
  const showCampaignTable = Boolean(
    lastPreview && !sharedSkip && (drafts.length > 0 || lsaMonitor),
  );
  const refreshButton = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={!tenantId || pacing.isFetching}
      onClick={() => void onRefresh()}
    >
      {pacing.isFetching ? (
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      ) : (
        <RefreshCw className="mr-2 h-4 w-4" />
      )}
      Refresh pacing
    </Button>
  );
  const saveSplitButton = sharedSkip ? null : (
    <Button
      type="button"
      size="sm"
      className="bg-coral hover:bg-coral-hover"
      disabled={!dirty || saveShares.isPending || drafts.length === 0}
      onClick={() => saveShares.mutate()}
    >
      {saveShares.isPending ? (
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      ) : (
        <Save className="mr-2 h-4 w-4" />
      )}
      Save split
    </Button>
  );

  return (
    <Card className="overflow-hidden">
      <CardHeader className="space-y-3">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              Campaign daily plan
            </p>
            <CardTitle className="mt-1 flex items-center gap-2">
              <Gauge className="h-4 w-4 text-coral" />
              Autonomous pacing
              <Badge variant="outline">dry-run</Badge>
            </CardTitle>
            <CardDescription>
              {sharedSkip
                ? 'Shared daily budget — pacing requires per-campaign budgets. This card does not write or save a mix.'
                : hasSharedPots
                  ? 'Shared pots stay parked. Exclusive campaigns below are still paced. This card never spends.'
                  : 'Core paces toward the daily figure in the row above on Mondays and Thursdays. Edit the split, then save. This card never spends.'}
              {planSavedAt ? (
                <>
                  {' '}
                  Last plan from{' '}
                  {new Date(planSavedAt).toLocaleString()}.
                </>
              ) : null}
              {' '}
              Next tick {nextTick.weekday} 08:00 {cronZoneLabel(nextTick.at)}.
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            {showCampaignTable ? null : refreshButton}
            <div className="flex flex-wrap items-end gap-2">
              <div className="space-y-1">
                <span className="block text-[11px] uppercase tracking-wider text-muted-foreground">
                  Ceiling N
                </span>
                <div className="flex items-center gap-2">
                  <Input
                    aria-label="Pacing ceiling N"
                    type="number"
                    min={1}
                    max={3}
                    step={0.1}
                    placeholder="unset"
                    value={draftN}
                    onChange={(event) => setDraftN(event.target.value)}
                    className="h-8 w-[5.5rem] tabular-nums"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!tenantId || saveCeiling.isPending || ceiling.isLoading}
                    onClick={() => saveCeiling.mutate()}
                  >
                    {saveCeiling.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="mr-2 h-4 w-4" />
                    )}
                    Save ceiling
                  </Button>
                </div>
                <span className="block text-[11px] text-muted-foreground">
                  × mapped daily from the saved split
                </span>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={
                  !tenantId || resetBaselines.isPending || ceiling.isLoading
                }
                onClick={() => resetBaselines.mutate()}
              >
                {resetBaselines.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                Recompute baselines
              </Button>
            </div>
            {showCampaignTable ? null : saveSplitButton}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {firstLoad ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Computing this account&apos;s pacing plan…
          </div>
        ) : pacing.isError && !lastPreview ? (
          <p className="text-sm text-destructive">{extractError(pacing.error)}</p>
        ) : lastPreview ? (
          <>
            {hasSharedPots ? (
              <SharedBudgetSkipPanel
                groups={sharedGroups}
                potMicros={sharedGroups.reduce(
                  (sum, group) => sum + (group.daily_budget_micros || 0),
                  0,
                )}
                exclusiveAlsoPaced={!sharedSkip}
              />
            ) : null}

            {sharedSkip ? null : (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  How the Ads target daily splits
                  {lsaMonitor ? ' — LSA is reserved separately' : ' across live campaigns'}
                </span>
                <span className="tabular-nums">
                  {Math.round(shareTotal * 1000) / 10}% allocated
                  {fixedCount > 0
                    ? ` · ${fixedCount} on a fixed monthly budget`
                    : ''}
                </span>
              </div>
              <div className="flex h-3 overflow-hidden rounded-full bg-teal-light">
                {drafts.map((row, index) => (
                  <div
                    key={row.campaign_id}
                    className={index % 2 === 0 ? 'bg-coral' : 'bg-coral-dark'}
                    style={{ width: `${Math.max(row.share * 100, 0)}%` }}
                    title={`${row.campaign_name} ${(row.share * 100).toFixed(1)}%`}
                  />
                ))}
              </div>
              {currentMicros > 0 ? (
                <div className="h-1 rounded-full bg-teal-light">
                  <div
                    className="h-1 rounded-full bg-teal"
                    style={{ width: `${Math.min(ratio, 1) * 100}%` }}
                  />
                </div>
              ) : null}
            </div>
            )}

            {sharedSkip ? null : refusal ? (
              <PacingRefusalPanel refusal={refusal} />
            ) : lastPreview.skip_reason ? (
              <p className="text-sm text-muted-foreground">
                {heldThisWindow
                  ? 'Held this window: the live daily stays until the next Monday or Thursday tick.'
                  : `Status: ${lastPreview.status} (${lastPreview.skip_reason})`}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                Status: {lastPreview.status}
                {lastPreview.remainder_micros
                  ? ` · remainder ${formatUsdFromMicros(lastPreview.remainder_micros)}`
                  : ''}
              </p>
            )}

            {lastPreview.requires_manual_pause ? (
              <div className="rounded-md border border-coral bg-coral-light px-3 py-2 text-sm text-coral-dark">
                <span className="font-medium">Please pause manually in Google Ads.</span>{' '}
                Cycle is out of budget but the account is still spending{' '}
                {formatUsdFromMicros(lastPreview.current_account_daily_micros)}/day.
                Core does not push $0 to a live campaign (breaks serving), so
                the daily keeps burning until an operator pauses the campaign
                by hand.
              </div>
            ) : null}

            {lastPreview.adjustments.some(
              (row) => row.skip_reason === 'missing_ceiling_n',
            ) ? (
              <p className="text-sm text-muted-foreground">
                PMax cannot raise until you set a ceiling (1.0–3.0 × mapped daily from the saved split).
              </p>
            ) : null}

            {sharedSkip ? null : drafts.length > 0 || lsaMonitor ? (
              <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-end gap-2">
                  {refreshButton}
                  {saveSplitButton}
                </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Campaign</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Share %</TableHead>
                    {/* CORE-BP-06: rename to match the tiles above. Same
                        columns, plain-English labels. */}
                    <TableHead className="text-right">Currently set</TableHead>
                    <TableHead className="text-right">Pace today</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {combine && lsaMonitor ? (
                    <>
                      <TableRow className="bg-warm-bg font-medium">
                        <TableCell>
                          Google
                          <div className="text-xs font-normal text-muted-foreground">
                            Ads + LSA, one budget
                          </div>
                          {googleSplit ? (
                            <div className="text-xs font-normal text-muted-foreground">
                              {formatDollars(googleSplit.total_monthly_dollars)} a month
                            </div>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">Total</Badge>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">100%</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {recalculating ? (
                            <span className="text-muted-foreground">Recalculating</span>
                          ) : combinedCurrentMicros != null ? (
                            formatUsdFromMicros(combinedCurrentMicros)
                          ) : (
                            <>
                              {formatUsdFromMicros(currentMicros)}
                              <span className="text-xs font-normal text-muted-foreground">, partial</span>
                            </>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {recalculating ? (
                            <span className="text-muted-foreground">Recalculating</span>
                          ) : (
                            <>
                              {formatUsdFromMicros(parentSuggestedKnown)}
                              {parentSuggestedPartial ? (
                                <span className="text-xs font-normal text-muted-foreground">, partial</span>
                              ) : null}
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          Google Ads
                          {googleSplit && typeof googleSplit.google_ads_monthly_dollars === 'number' ? (
                            <div className="text-xs text-muted-foreground">
                              {formatDollars(googleSplit.google_ads_monthly_dollars)} a month
                            </div>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">Ads</Badge>
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">
                          {adsPercentOfGoogle != null ? `${adsPercentOfGoogle}% of Google` : '—'}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatUsdFromMicros(currentMicros)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatUsdFromMicros(suggestedAdsMicros)}
                        </TableCell>
                      </TableRow>
                    </>
                  ) : null}
                  {drafts.map((row) => (
                    <TableRow key={row.campaign_id}>
                      <TableCell className={combine ? 'pl-6' : undefined}>
                        {row.campaign_name}
                        {row.relearn_clamped ? (
                          <span className="ml-2 text-xs text-muted-foreground">
                            ±15% clamp
                          </span>
                        ) : null}
                        {row.ceiling_bound && row.ceiling_max_micros ? (
                          <span className="ml-2 text-xs text-muted-foreground">
                            ceiling {formatUsdFromMicros(row.ceiling_max_micros)}
                          </span>
                        ) : row.ceiling_max_micros ? (
                          <span className="ml-2 text-xs text-muted-foreground">
                            max {formatUsdFromMicros(row.ceiling_max_micros)}
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {campaignChannelLabel(row.advertising_channel_type)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {row.fixed_monthly_usd != null ? (
                          // On its own monthly budget. Showing an editable 0%
                          // here would be a control that silently does nothing:
                          // the fixed figure wins, and a percentage typed
                          // against it is never applied.
                          <span
                            className="inline-flex items-center justify-end gap-1 text-xs text-muted-foreground"
                            title="This campaign has its own monthly budget, set in Campaign budgets below. It does not take a share of the account total."
                          >
                            fixed{' '}
                            {row.fixed_monthly_usd.toLocaleString(undefined, {
                              style: 'currency',
                              currency: 'USD',
                              maximumFractionDigits: 0,
                            })}
                            /mo
                          </span>
                        ) : (
                        <div className="inline-flex items-center justify-end gap-1">
                          <Input
                            aria-label={`${row.campaign_name} share percent`}
                            type="number"
                            min={0}
                            max={100}
                            step={0.1}
                            value={Math.round(row.share * 1000) / 10}
                            onChange={(event) => {
                              const raw = event.target.value;
                              if (raw === '') {
                                setDrafts(
                                  setShareKeepingHundred(
                                    drafts,
                                    row.campaign_id,
                                    0,
                                    targetMicros,
                                  ),
                                );
                                return;
                              }
                              const next = Number.parseFloat(raw);
                              if (!Number.isFinite(next)) return;
                              setDrafts(
                                setShareKeepingHundred(
                                  drafts,
                                  row.campaign_id,
                                  next / 100,
                                  targetMicros,
                                ),
                              );
                            }}
                            className="h-8 w-[4.5rem] text-right tabular-nums"
                          />
                          <span className="text-xs text-muted-foreground">%</span>
                        </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatUsdFromMicros(row.current_amount_micros)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Input
                          aria-label={`${row.campaign_name} proposed daily`}
                          type="number"
                          min={0}
                          step={0.01}
                          value={(row.proposed_amount_micros / 1_000_000).toFixed(2)}
                          onChange={(event) => {
                            const next = Number.parseFloat(event.target.value);
                            if (!Number.isFinite(next)) return;
                            setDrafts(
                              setDailyKeepingHundred(
                                drafts,
                                row.campaign_id,
                                next,
                                targetMicros,
                              ),
                            );
                          }}
                          className="ml-auto h-8 w-[7rem] text-right tabular-nums"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  {lsaMonitor ? (
                    <TableRow>
                      <TableCell>
                        Google LSA
                        {lsaMonitor.customer_id ? (
                          <span className="text-xs text-muted-foreground">
                            {' '}
                            · CID {lsaMonitor.customer_id}
                          </span>
                        ) : null}
                        {combine && googleSplit && typeof googleSplit.google_lsa_monthly_dollars === 'number' ? (
                          <div className="text-xs text-muted-foreground">
                            {formatDollars(googleSplit.google_lsa_monthly_dollars)} a month
                          </div>
                        ) : null}
                        {/* The write outcome used to live on the monitor panel; the row is
                            the only place left, so it must not assert "not written" when
                            Core did write (Viktor, #2605). */}
                        {lsaWriteBlock?.write_status === 'success' ? (
                          <span className="ml-2 text-xs text-teal-dark">
                            {typeof lsaWriteBlock.proposed_amount_micros === 'number'
                              ? `Core set ${formatUsdFromMicros(lsaWriteBlock.proposed_amount_micros)}/day`
                              : 'Core set the LSA daily'}
                          </span>
                        ) : lsaWriteBlock?.write_status === 'dry_run' &&
                          typeof lsaWriteBlock.proposed_amount_micros === 'number' ? (
                          <span className="ml-2 text-xs text-muted-foreground">
                            Core would set {formatUsdFromMicros(lsaWriteBlock.proposed_amount_micros)}/day, dry run
                          </span>
                        ) : lsaWriteBlock?.write_status === 'error' ? (
                          <span
                            className="ml-2 text-xs text-destructive"
                            title={lsaWriteBlock.error ?? undefined}
                          >
                            Core write failed
                          </span>
                        ) : (
                          <span className="ml-2 text-xs text-muted-foreground">
                            {lsaSkipLabel(lsaWriteBlock)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">LSA</Badge>
                      </TableCell>
                      {/* LSA is never part of the Ads split; when totals are combined, say what slice of the combined current it holds. */}
                      <TableCell
                        className="text-right tabular-nums text-muted-foreground"
                        title={combine ? undefined : 'Reserved separately, not part of the Ads split'}
                      >
                        {combine && lsaPercentOfGoogle != null
                          ? `${lsaPercentOfGoogle}% of Google`
                          : combine &&
                              lsaCurrentMicros != null &&
                              lsaCurrentMicros > 0 &&
                              combinedCurrentMicros != null &&
                              combinedCurrentMicros > 0
                            ? `${Math.round((lsaCurrentMicros / combinedCurrentMicros) * 100)}% of combined, reserved`
                            : '—'}
                        {combine && onChangeSplit ? (
                          <div>
                            <Button
                              type="button"
                              variant="link"
                              size="sm"
                              className="h-auto p-0 text-xs text-coral"
                              onClick={onChangeSplit}
                            >
                              Change split
                            </Button>
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {recalculating ? (
                          <span className="text-muted-foreground">Recalculating</span>
                        ) : typeof lsaCurrentMicros === 'number' ? (
                          formatUsdFromMicros(lsaCurrentMicros)
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {recalculating ? (
                          <span className="text-muted-foreground">Recalculating</span>
                        ) : (
                          <>
                            {lsaMonitor.available === false
                              ? '—'
                              : formatUsdFromMicros(
                                  // Same source as the Suggested daily tile at the top of the page: the
                                  // writer's plan when Core manages it, else the reservation.
                                  lsaPlanMicros ?? lsaMonitor.implied_daily_micros,
                                )}
                            <div className="text-[11px] text-muted-foreground">
                              {lsaMonitor.available === false
                                ? 'Pacing paused'
                                : 'Planner reservation'}
                            </div>
                            {lsaMonitor.available !== false &&
                            typeof lsaMonitor.weekly_recommendation_micros === 'number' ? (
                              <div className="text-[11px] text-teal-dark">
                                Suggest{' '}
                                {formatWholeUsdFromMicros(
                                  lsaMonitor.weekly_recommendation_micros,
                                )}{' '}
                                / week
                              </div>
                            ) : null}
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No live campaigns with a daily budget to pace.
              </p>
            )}
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}
